// haushaltUmzug.ts — A4 (Plan 2026-09-30): Zieht der Hauptkontakt um (seine
// Standardanschrift ändert sich), fragt die Akte: „Auch bei den
// Haushaltsmitgliedern ändern?" — jedes Mitglied einzeln abwählbar
// (Entscheidung des Users 30.09.2026). Übernommen wird serverseitig; die
// bisherige Anschrift der Mitglieder bleibt als weitere Anschrift erhalten.

import { apiFetch } from "./apiFetch";
import { haushaltsanschriftUebernehmen } from "../components/relationsInline/persistRelations";

type Felder = Record<string, unknown>;

/** Die Anschriftfelder der Anlage-Maske — mit ihren Alias-Namen. */
const ANSCHRIFT_FELDER: string[][] = [
  ["street"],
  ["postalcode", "postalCode", "plz"],
  ["city", "ort"],
  ["cityArea", "ortsteil"],
  ["country", "land"],
];

const wert = (f: Felder, namen: string[]): string => {
  for (const n of namen) {
    const v = String(f[n] ?? "").trim();
    if (v) return v.toLowerCase().replace(/\s+/g, " ");
  }
  return "";
};

/**
 * Hat sich die Standardanschrift geändert — und steht danach eine (Straße,
 * PLZ oder Ort)? Wer die Anschrift nur leert, zieht nicht um.
 */
export function anschriftGeaendert(geladen: Felder, aktuell: Felder): boolean {
  const neu = ANSCHRIFT_FELDER.slice(0, 3).some((namen) => wert(aktuell, namen) !== "");
  if (!neu) return false;
  return ANSCHRIFT_FELDER.some((namen) => wert(geladen, namen) !== wert(aktuell, namen));
}

export type HaushaltsMitglied = { id: number; name: string | null };

/** Die (lesbaren) Haushaltsmitglieder — ohne den Kontakt selbst. */
export async function haushaltsMitgliederLaden(
  contactId: number,
  laden: (id: number) => Promise<{ mitglieder?: Array<{ id?: number; name?: string | null }> }> = async (id) =>
    (await apiFetch<{ mitglieder?: Array<{ id?: number; name?: string | null }> }>(`contact-relation/haushalt/${id}`)).data ?? {},
): Promise<HaushaltsMitglied[]> {
  try {
    const d = await laden(contactId);
    return (d.mitglieder ?? [])
      .filter((m): m is { id: number; name?: string | null } => typeof m?.id === "number" && m.id !== contactId)
      .map((m) => ({ id: m.id, name: m.name ?? null }));
  } catch {
    return [];
  }
}

/** Überträgt die Anschrift auf die gewählten Mitglieder; liefert die Fehlschläge im Klartext. */
export async function umzugAnwenden(
  contactId: number,
  mitglieder: HaushaltsMitglied[],
  uebernehmen: (zielId: number, vonId: number) => Promise<boolean> = haushaltsanschriftUebernehmen,
): Promise<string[]> {
  const fehler: string[] = [];
  for (const m of mitglieder) {
    if (!(await uebernehmen(m.id, contactId))) {
      fehler.push(`Die neue Anschrift konnte für „${m.name ?? `Kontakt ${m.id}`}" nicht übernommen werden.`);
    }
  }
  return fehler;
}
